import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { brand, radius } from './brand';

interface ProgressTrackerProps {
    // credentialType values, in the order the page offers them.
    required: string[];
    issued: string[];
}

export const ProgressTracker: FC<ProgressTrackerProps> = ({required, issued}) => {
    const {t} = useTranslation();
    const done = required.filter(credentialType => issued.includes(credentialType));
    const complete = done.length === required.length;

    return (
        <Box
            data-testid="progress-tracker"
            sx={{
                background: brand.backgroundAlt,
                border: `1px solid ${complete ? brand.green : brand.border}`,
                borderRadius: radius.card,
                padding: '20px 22px',
            }}>
            <Stack spacing={1.75}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '1rem'}}>
                        {t('progress.title')}
                    </Typography>
                    <Typography
                        data-testid="progress-counter"
                        sx={{
                            color: complete ? brand.navy : brand.blue,
                            fontWeight: 700,
                            fontSize: '.95rem',
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                        {t('progress.counter', {issued: done.length, total: required.length})}
                    </Typography>
                </Stack>

                <Stack spacing={0.75}>
                    {required.map(credentialType => {
                        const isIssued = issued.includes(credentialType);
                        return (
                            <Stack key={credentialType} direction="row" spacing={1.25} alignItems="center">
                                {isIssued
                                    ? <CheckCircleIcon sx={{fontSize: 19, color: brand.blue}}/>
                                    : <RadioButtonUncheckedIcon sx={{fontSize: 19, color: brand.border}}/>}
                                <Typography sx={{
                                    color: isIssued ? brand.navy : brand.text,
                                    fontSize: '.95rem',
                                    fontWeight: isIssued ? 600 : 400,
                                }}>
                                    {t(`credentials.${credentialType}.title`)}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>

                <Typography sx={{color: brand.text, fontSize: '.9rem', lineHeight: 1.55}}>
                    {complete ? t('progress.complete') : t('progress.incomplete')}
                </Typography>
            </Stack>
        </Box>
    );
};
